import React from 'react';
import styled, { keyframes } from 'styled-components';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { HeaderContainer } from 'components/PageHeader';
import {
  Container,
  MetricsGrid,
  DashboardLayout,
  MainContent,
  Sidebar,
} from './StyledComponents';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Base skeleton element with theme-aware gradient
const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? `linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.03) 0%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.03) 100%
        )`
      : `linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.04) 0%,
          rgba(0, 0, 0, 0.08) 50%,
          rgba(0, 0, 0, 0.04) 100%
        )`};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.radius || '4px'};
`;

const SkeletonTitle = styled(SkeletonBase)`
  height: 36px;
  width: 180px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    height: 28px;
    width: 140px;
  }
`;

const SkeletonMetricCard = styled(Card)`
  padding: 24px;
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const SkeletonMetricValue = styled(SkeletonBase)`
  height: 32px;
  width: 120px;
  margin-bottom: 8px;
`;

const SkeletonMetricLabel = styled(SkeletonBase)`
  height: 16px;
  width: 100px;
  margin-bottom: 4px;
`;

const SkeletonMetricChange = styled(SkeletonBase)`
  height: 14px;
  width: 80px;
`;

const SkeletonSearchBar = styled(SkeletonBase)`
  height: 44px;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SkeletonFilterButton = styled(SkeletonBase)`
  height: 36px;
  width: 120px;
  border-radius: 20px;
  display: inline-block;
  margin-right: 8px;
`;

const SkeletonTableHeader = styled(SkeletonBase)`
  height: 48px;
  width: 100%;
  border-radius: 8px 8px 0 0;
`;

const SkeletonTableRow = styled(SkeletonBase)`
  height: 60px;
  width: 100%;
  margin-bottom: 1px;
  border-radius: 0;
`;

const SkeletonSmartInsights = styled(Card)`
  padding: 20px;
  height: 200px;
`;

const SkeletonQuickActions = styled(Card)`
  padding: 20px;
  height: 180px;
`;

const SkeletonSegmentBreakdown = styled(Card)`
  padding: 20px;
  height: 300px;
`;

const SkeletonButton = styled(SkeletonBase)`
  height: 40px;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const SkeletonSegmentBar = styled(SkeletonBase)`
  height: 40px;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const CustomersSkeleton = () => {
  return (
    <EnhancedThemeProvider>
      <Container>
        {/* Header Skeleton */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <SkeletonBase
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                flexShrink: 0,
              }}
            />
            <SkeletonTitle />
          </HeaderContainer>
        </Card>

        {/* Metrics Grid Skeleton */}
        <MetricsGrid>
          {[1, 2, 3, 4, 5].map((index) => (
            <SkeletonMetricCard key={index}>
              <div>
                <SkeletonMetricValue />
                <SkeletonMetricLabel />
              </div>
              <SkeletonMetricChange />
            </SkeletonMetricCard>
          ))}
        </MetricsGrid>

        {/* Dashboard Layout Skeleton */}
        <DashboardLayout>
          <MainContent>
            {/* Search Section Skeleton */}
            <Card style={{ marginBottom: '16px', padding: '20px' }}>
              <SkeletonSearchBar />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <SkeletonFilterButton key={index} />
                ))}
              </div>
            </Card>

            {/* Table Skeleton */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <SkeletonTableHeader />
              {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                <SkeletonTableRow key={index} />
              ))}
            </Card>
          </MainContent>

          <Sidebar>
            {/* Smart Insights Skeleton */}
            <SkeletonSmartInsights>
              <SkeletonBase
                style={{ height: '20px', width: '80px', marginBottom: '12px' }}
              />
              <SkeletonBase
                style={{ height: '24px', width: '100%', marginBottom: '8px' }}
              />
              <SkeletonBase style={{ height: '60px', width: '100%' }} />
            </SkeletonSmartInsights>

            {/* Quick Actions Skeleton */}
            <SkeletonQuickActions>
              <SkeletonBase
                style={{ height: '20px', width: '100px', marginBottom: '16px' }}
              />
              <SkeletonButton />
              <SkeletonButton />
              <SkeletonButton />
            </SkeletonQuickActions>

            {/* Segment Breakdown Skeleton */}
            <SkeletonSegmentBreakdown>
              <SkeletonBase
                style={{ height: '20px', width: '140px', marginBottom: '16px' }}
              />
              {[1, 2, 3, 4, 5].map((index) => (
                <SkeletonSegmentBar key={index} />
              ))}
            </SkeletonSegmentBreakdown>
          </Sidebar>
        </DashboardLayout>
      </Container>
    </EnhancedThemeProvider>
  );
};

export default CustomersSkeleton;
